import { Card, Disclosure, DisclosureChevron, Feed } from 'corso-admin-ui';
import { formatter } from 'corso-web-lib';
import { formatRelative } from 'date-fns';
import { useState } from 'react';
import { useShippingClaimMessagesQuery } from '~/features/shippingClaimReview/hooks/useShippingClaimMessagesQuery';
import { useIsCorsoCxAgent } from '~/hooks/useIsCorsoAdmin';
import { useIsConciergeWidget } from '~/hooks/widgetHooks';

function CorsoIcon() {
  return (
    <svg
      className="h-full"
      xmlns="http://www.w3.org/2000/svg"
      version="1.1"
      fill="currentColor"
      viewBox="0 0 200 234.59"
    >
      <g>
        <path d="M0,117.3c0,58.36,42.95,106.67,98.96,115.09v-62.37c-22.13-7.31-38.12-28.13-38.12-52.72s15.99-45.4,38.12-52.72V2.21 C42.95,10.62,0,58.94,0,117.3z" />
        <path d="M161.04,76.44c17.83,0,32.29-14.46,32.29-32.29c0-17.83-14.46-32.29-32.29-32.29s-32.29,14.46-32.29,32.29 C128.75,61.98,143.2,76.44,161.04,76.44z" />
        <circle cx="161.04" cy="190.45" r="32.29" />
      </g>
    </svg>
  );
}

function ContentIFrame({ title, srcDoc }: { title: string; srcDoc: string }) {
  const [height, setHeight] = useState<number>();
  return (
    <iframe
      style={{
        '--iframe-height': height ? `${height}px` : 'auto',
      }}
      onLoad={(e) =>
        setHeight(e.currentTarget.contentDocument?.body.scrollHeight)
      }
      className="h-[calc(var(--iframe-height)*1.01)] min-h-(--iframe-height) w-full"
      title={title}
      srcDoc={srcDoc}
    />
  );
}

export function ShippingClaimMessages() {
  const { data, isPending } = useShippingClaimMessagesQuery();
  const isCorsoCxAgent = useIsCorsoCxAgent();
  const isCorsoConcierge = useIsConciergeWidget();

  if (isCorsoConcierge) return null;

  if (isPending) return null;

  if (!data) return null;

  const messages = data.messages.map((message) => ({
    id: message.createdOn,
    iconSize: '32px',
    kind: 'message' as const,
    from: message.from,
    createdOn: message.createdOn,
    messageBody: message.sanitizedHtmlBody,
  }));

  return (
    <Card>
      <Disclosure
        defaultOpen={!isCorsoCxAgent}
        renderSummary={
          <Card.Line>
            <Card.Heading>Concierge Messages</Card.Heading>
            <DisclosureChevron />
          </Card.Line>
        }
      >
        <div className="mt-4 space-y-2 rounded-md border border-neutral-200 p-3 shadow-xs lg:p-4">
          <div className="space-y-4">
            <dl className="grid grid-cols-[auto_1fr] gap-2 text-xs">
              <dt className="font-semibold">Subject</dt>
              <dd className="break-words">{data.subject}</dd>
              <dt className="font-semibold">Customer</dt>
              <dd className="break-words">
                {data.customerName ? (
                  <>
                    {data.customerName}{' '}
                    <a
                      href={`mailto:${data.customerEmail}`}
                      className="text-right text-neutral-500 hover:underline"
                    >
                      {data.customerEmail}
                    </a>
                  </>
                ) : (
                  <a
                    href={`mailto:${data.customerEmail}`}
                    className="text-right text-neutral-500 hover:underline"
                  >
                    {data.customerEmail}
                  </a>
                )}
              </dd>
            </dl>
            <Feed
              events={messages}
              renderIcon={(message) => {
                if (message.from === data.customerEmail)
                  return (
                    <div className="flex h-8 w-8 items-center justify-center rounded-full border bg-white font-bold text-neutral-500">
                      {(data.customerName ?? data.customerEmail).charAt(0)}
                    </div>
                  );
                return (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full border bg-white p-2 text-neutral-500">
                    <CorsoIcon />
                  </div>
                );
              }}
            >
              {(message) => {
                const customerNameOrEmail =
                  message.from === data.customerEmail && data.customerName
                    ? data.customerName
                    : message.from;

                const title = `Message from ${customerNameOrEmail} on ${formatter.date(
                  message.createdOn,
                )}`;

                return (
                  <div className="w-full">
                    <div className="flex justify-between rounded-t-md border p-2 text-xs">
                      <span className="font-bold">{customerNameOrEmail}</span>
                      <time
                        dateTime={message.createdOn}
                        className="text-neutral-500"
                      >
                        {formatRelative(
                          new Date(message.createdOn),
                          new Date(),
                        )}
                      </time>
                    </div>
                    <div className="rounded-b-md border-x border-b">
                      <ContentIFrame
                        title={title}
                        srcDoc={`<!DOCTYPE html><html><head><title>${title}</title><style>body{background:white;padding:1rem;font-family:Arial;font-size:100%;}</style></head><body>${message.messageBody}</body></html>`}
                      />
                    </div>
                  </div>
                );
              }}
            </Feed>
          </div>
        </div>
      </Disclosure>
    </Card>
  );
}
